import { reactive } from 'vue';

/**
 * Aceleres 2026-08-18: resolve as mídias servidas pelo endpoint guardado
 * (`/attachment_downloads/<id>`), que o navegador NÃO consegue abrir sozinho.
 *
 * Essa URL não é um arquivo: é uma rota da API que reavalia a permissão a cada
 * request (ver AttachmentDownloadsController) e por isso exige o cabeçalho de
 * autenticação. Posta direta num `<img src>`, o navegador pede sem cabeçalho e
 * leva 401 — medido no homolog. O jeito de desenhá-la é baixar pelo cliente
 * autenticado e criar um object URL.
 *
 * Isto vivia inteiro dentro do ContactInfo.vue. Passou para cá quando a tira de
 * miniaturas da galeria precisou da mesma coisa: era ali que as fotos antigas
 * "desapareciam" do histórico, porque a miniatura levava 401 e o item era
 * descartado. Uma segunda cópia da fila, do teto e do retry sairia de sincronia
 * com esta na primeira correção feita só de um lado.
 *
 * O que a fila resolve: uma conversa com centenas de mídias dispararia
 * centenas de downloads de uma vez. Com limite de concorrência a lista inteira
 * acaba desenhada, por partes, em vez de parar num teto.
 */

// Quantos downloads correm ao mesmo tempo.
const MAX_CONCURRENT_DOWNLOADS = 8;
// Teto só para o arquivo ORIGINAL (vídeo, e imagem sem variante): cada um pesa
// megabytes. A miniatura (`?variant=thumb`) tem poucos KB e não precisa de teto
// — precisa da fila.
const MAX_ORIGINAL_DOWNLOADS = 60;
const RETRY_DELAY_MS = 2000;

export const isGuardedMediaUrl = url =>
  /\/attachment_downloads\//.test(url || '');

export const isThumbVariantUrl = url => /[?&]variant=thumb\b/.test(url || '');

/**
 * Pede a variante reduzida da imagem em vez do arquivo inteiro. Só faz sentido
 * no endpoint guardado — é ele que sabe gerar a variante.
 */
export const withThumbVariant = url => {
  if (!url || !isGuardedMediaUrl(url)) return url;
  if (isThumbVariantUrl(url)) return url;

  return `${url}${url.includes('?') ? '&' : '?'}variant=thumb`;
};

/**
 * Aceleres 27/08/2026: pede a derivada AAC/MP4 do audio em vez do arquivo
 * original -- a unica que o WebKit do iPhone reproduz. So faz sentido no
 * endpoint guardado, que e quem sabe escolher entre as duas; o download
 * continua entregando o original (ver AttachmentDownloadsController).
 */
export const withPlaybackVariant = url => {
  if (!url || !isGuardedMediaUrl(url)) return url;
  if (/[?&]variant=playback\b/.test(url)) return url;

  return `${url}${url.includes('?') ? '&' : '?'}variant=playback`;
};

export function useGuardedMedia({
  requestTimeoutMs = 0,
  maxConcurrentOriginalDownloads = MAX_CONCURRENT_DOWNLOADS,
} = {}) {
  // Reativos: o template chama `guardedMediaSrc(url)` durante o render e
  // precisa voltar a desenhar quando o download termina.
  const resolvedUrls = reactive({});
  const failed = reactive({});
  const blobs = reactive({});

  const pending = {};
  const queue = [];
  // Contadores num objeto, e nao em `let`: a continuacao de cada download e
  // criada dentro do laco da fila e precisa fechar sobre eles.
  const counters = {
    activeDownloads: 0,
    activeOriginalDownloads: 0,
    originalDownloads: 0,
  };

  const guardedRequest = (client, url) => {
    const requestOptions = { responseType: 'blob' };
    if (!requestTimeoutMs) return client.get(url, requestOptions);

    const controller = new AbortController();
    const request = client.get(url, {
      ...requestOptions,
      signal: controller.signal,
    });
    let timeoutId;
    const timeout = new Promise((_resolve, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        reject(new Error('Tempo limite ao carregar mídia protegida'));
      }, requestTimeoutMs);
    });

    return Promise.race([request, timeout]).finally(() =>
      clearTimeout(timeoutId)
    );
  };

  // O blob fica guardado porque a prévia do PDF (pdf.js) e o download precisam
  // dos bytes, e não da URL — baixar de novo seria o mesmo arquivo outra vez.
  const fetchBlob = (client, url, { retry }) =>
    guardedRequest(client, url)
      .then(({ data }) => {
        blobs[url] = data;
        return URL.createObjectURL(data);
      })
      .catch(() => {
        // Vídeo pesa muito mais que imagem e, sob servidor carregado, esbarra
        // no timeout com facilidade. Sem retentativa essa falha passageira
        // condenava o item até um F5, mesmo depois de o servidor normalizar.
        if (!retry) return '';

        return new Promise(resolve => {
          setTimeout(
            () => resolve(fetchBlob(client, url, { retry: false })),
            RETRY_DELAY_MS
          );
        });
      });

  // Cada download que termina destrava o proximo, entao a lista inteira acaba
  // desenhada, por partes, em vez de parar num teto.
  function drainQueue() {
    while (
      counters.activeDownloads < MAX_CONCURRENT_DOWNLOADS &&
      queue.length
    ) {
      // A tira móvel pode misturar fotos pequenas com vídeos grandes. As
      // variantes de miniatura entram primeiro e os originais têm um limite
      // próprio, para que dois vídeos lentos não escondam todas as fotos que
      // já poderiam ser desenhadas.
      const thumbnailIndex = queue.findIndex(({ url }) =>
        isThumbVariantUrl(url)
      );
      const nextIndex = thumbnailIndex >= 0 ? thumbnailIndex : 0;
      const nextRequest = queue[nextIndex];
      const isOriginal = !isThumbVariantUrl(nextRequest.url);

      if (
        isOriginal &&
        counters.activeOriginalDownloads >= maxConcurrentOriginalDownloads
      ) {
        break;
      }

      const [{ url, resolve }] = queue.splice(nextIndex, 1);
      counters.activeDownloads += 1;
      if (isOriginal) counters.activeOriginalDownloads += 1;

      fetchBlob(window.axios, url, { retry: true })
        .then(objectUrl => {
          if (objectUrl) resolvedUrls[url] = objectUrl;
          else failed[url] = true;
          return objectUrl;
        })
        .catch(() => {
          failed[url] = true;
          return '';
        })
        .then(objectUrl => {
          counters.activeDownloads -= 1;
          if (isOriginal) counters.activeOriginalDownloads -= 1;
          delete pending[url];
          resolve(objectUrl || '');
          drainQueue();
        });
    }
  }

  const resolveGuardedMedia = url => {
    if (!isGuardedMediaUrl(url)) return Promise.resolve(url || '');
    if (resolvedUrls[url]) return Promise.resolve(resolvedUrls[url]);
    if (pending[url]) return pending[url];
    if (failed[url]) return Promise.resolve('');

    if (!isThumbVariantUrl(url)) {
      if (counters.originalDownloads >= MAX_ORIGINAL_DOWNLOADS) {
        failed[url] = true;
        return Promise.resolve('');
      }
      counters.originalDownloads += 1;
    }

    if (!window.axios) {
      failed[url] = true;
      return Promise.resolve('');
    }

    const request = new Promise(resolve => {
      queue.push({ url, resolve });
    });

    pending[url] = request;
    drainQueue();

    return request;
  };

  /**
   * Para usar direto no `:src`. Dispara a resolução no primeiro render que
   * pedir a URL e devolve vazio até o download terminar — a saída em
   * `Promise.resolve()` tira a escrita do estado de dentro do render.
   */
  const guardedMediaSrc = url => {
    if (!url) return '';
    if (!isGuardedMediaUrl(url)) return url;

    const resolved = resolvedUrls[url];
    if (!resolved && !pending[url] && !failed[url]) {
      Promise.resolve().then(() => resolveGuardedMedia(url));
    }

    return resolved || '';
  };

  const guardedMediaFailed = url => Boolean(failed[url]);
  const guardedMediaBlob = url => blobs[url];

  /**
   * Reabre uma URL que ja esgotou as tentativas automaticas. A acao e
   * deliberada (botao "Tentar novamente"), por isso ela tambem devolve uma
   * vaga ao orcamento de originais antes de entrar novamente na fila.
   */
  const retryGuardedMedia = url => {
    if (!isGuardedMediaUrl(url)) return Promise.resolve(url || '');
    if (pending[url]) return pending[url];

    const hadFailed = Boolean(failed[url]);
    if (resolvedUrls[url]) {
      URL.revokeObjectURL(resolvedUrls[url]);
      delete resolvedUrls[url];
    }
    delete blobs[url];
    delete failed[url];

    if (
      hadFailed &&
      !isThumbVariantUrl(url) &&
      counters.originalDownloads > 0
    ) {
      counters.originalDownloads -= 1;
    }

    return resolveGuardedMedia(url);
  };

  /**
   * O orçamento é POR CONVERSA. Sem isto, o teto de originais e as falhas
   * acumulavam para toda a vida do componente: passadas ~60 prévias, toda a
   * mídia seguinte era marcada como falhada — "vai desaparecendo" à medida que
   * se percorrem conversas. Os object URLs já baixados não são descartados de
   * propósito: voltar a uma conversa vista há pouco desenha na hora.
   */
  const resetGuardedMediaBudget = () => {
    counters.originalDownloads = 0;
    Object.keys(failed).forEach(key => delete failed[key]);
  };

  const releaseGuardedMedia = () => {
    Object.values(resolvedUrls).forEach(objectUrl =>
      URL.revokeObjectURL(objectUrl)
    );
  };

  return {
    isGuardedMediaUrl,
    isThumbVariantUrl,
    withThumbVariant,
    guardedMediaSrc,
    guardedMediaFailed,
    guardedMediaBlob,
    retryGuardedMedia,
    resolveGuardedMedia,
    resetGuardedMediaBudget,
    releaseGuardedMedia,
  };
}
